"use client";

import React, { JSX } from "react";

import ReactDOMServer from "react-dom/server";

import { LinkHandler } from "@/components/common/LinkHandler";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { useClickedButtonPending } from "@/hooks/useClickedButtonPending";
import { useNavigation } from "@/hooks/useNavigation";
import { NavigationClickedData } from "@/types/ga4";
import { GA4 } from "@/utils/ga4Utils";
import { segmentEvent } from "@/utils/segmentfunctions";
import { ThemeTypes } from "@/utils/theming";

import {
  ArrowIconPrimary,
  ArrowIconTextlink,
  LoadingIconPrimary,
  LoadingIconSecondary,
  LoadingIconTertiary,
  LoadingIconTextlink,
} from "./Buttonicons";

export type ButtonLevel = "Primary" | "Secondary" | "Tertiary" | "Quaternary" | "Textlink";
export type ButtonIcon = "Left" | "Right" | "Only";
export type ButtonSize = "Default" | "Small";
export type Type = "submit" | "button";

interface TrackObject {
  name?: string;
  text?: string;
  destination?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

/**
 * @todo - Once there are no more instances of ButtonNova with both ctaLink and onClick:
 *  1. Update ButtonNova type definition to not allow both ctaLink and onClick.
 *  2. Remove button-no-ctalink-and-onclick.mjs eslint rule.
 */
export interface ButtonNovaProps extends React.HTMLAttributes<HTMLButtonElement> {
  theme?: ThemeTypes;
  level?: ButtonLevel;
  icon?: ButtonIcon;
  size?: ButtonSize;
  type?: Type;
  ctaLink?: string;
  width?: string;
  className?: string;
  label: string;
  srlabel?: string;
  loading?: boolean;
  disabled?: boolean;
  trackObject?: TrackObject;
  onClick?: React.MouseEventHandler<HTMLElement>;
  copyClassName?: string;
  customIcon?: React.ReactNode;
  children?: React.ReactNode;
  fontColor?: string;
  onDarkBackground?: boolean;
  onImageBackground?: boolean;
}

const LoadingIconMap: Record<ButtonLevel, JSX.Element> = {
  Primary: <LoadingIconPrimary />,
  Secondary: <LoadingIconSecondary />,
  Tertiary: <LoadingIconTertiary />,
  Quaternary: <LoadingIconSecondary />,
  Textlink: <LoadingIconTextlink />,
};

const ArrowIconMap: Partial<Record<ButtonLevel, JSX.Element>> = {
  Textlink: <ArrowIconTextlink />,
};

export const ButtonNova: React.FC<ButtonNovaProps> = ({
  theme = "master",
  level = "Primary",
  icon,
  size = "Default",
  ctaLink,
  width = "auto",
  type = "button",
  className = "",
  label,
  srlabel,
  loading = false,
  disabled = false,
  trackObject,
  onClick,
  copyClassName = "",
  children,
  customIcon,
  onDarkBackground = false, // for use with a Secondary button on a dark background
  onImageBackground = false, // for use with a Secondary button on an image background
  fontColor = "",
  ...props
}) => {
  const navigate = useNavigation();
  const { pending, runClick, holdForDestination } = useClickedButtonPending();
  const isDisabled = disabled || pending;
  const LoadingIcon = LoadingIconMap[level] || <LoadingIconSecondary />;
  const ArrowIcon = ArrowIconMap[level] || <ArrowIconPrimary />;
  const CustomIcon = customIcon || null;
  const disabledCls = isDisabled ? "disabled" : "";
  let clsName = size === "Small" ? "CTA-10-Dia-Normal" : "CTA-20-Dia-Normal";
  const isMen = theme === "men";

  if (isMen) {
    clsName = size === "Small" ? "CTA-10-Dia-Mono uppercase" : "CTA-20-Dia-Mono uppercase";
  }

  const getTextFromChildren = (children: React.ReactNode): string => {
    if (!children) return "";
    // Render children to HTML string
    const html = ReactDOMServer.renderToStaticMarkup(<>{children}</>);
    // Strip all HTML tags
    return html.replace(/<[^>]+>/g, "");
  };

  const buildSegmentEventObject = () => {
    if (!trackObject) return null;
    const obj = { type: ctaLink ? "link" : "button", ...trackObject };
    if (ctaLink) {
      obj.destination = trackObject.destination ?? ctaLink;
    }
    obj.text =
      trackObject.text ||
      (label ? label.replace(/(<([^>]+)>)/gi, "") : getTextFromChildren(children)) ||
      "Link";
    delete obj.name;
    return obj;
  };

  const fireSegmentEvent = (
    eventName: string,
    obj: NonNullable<ReturnType<typeof buildSegmentEventObject>>,
    callback?: () => void
  ) => {
    const eventObject = {
      destination: obj.destination ?? "",
      text: obj.text,
      type: obj.type as NavigationClickedData["type"],
      ...(trackObject?.module_location && { module_location: trackObject.module_location }),
    };
    if (eventName === "Navigation Clicked") {
      GA4.onNavigationClicked(eventObject);
      callback?.();
      return;
    }
    if (eventName === "Outbound Link Clicked") {
      GA4.onOutboundLinkClicked(eventObject);
      callback?.();
      return;
    }
    if (eventName === "Marketing CTA Clicked") {
      GA4.onMarketingCtaClicked(eventObject);
      callback?.();
      return;
    }

    segmentEvent(eventName, obj, callback);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isDisabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    runClick(event, () => {
      // When segment event + ctaLink: stop navigation on LinkHandler, call segmentEvent, then finally navigate
      if (trackObject && ctaLink) {
        event.preventDefault();
        holdForDestination(ctaLink);
        const obj = buildSegmentEventObject();
        if (obj) {
          fireSegmentEvent(trackObject.name ?? "Marketing CTA Clicked", obj, () => {
            navigate(ctaLink);
          });
        }
        onClick?.(event);
        return;
      }

      // Standard segment event without navigation (i.e. for a button)
      if (trackObject) {
        try {
          const obj = buildSegmentEventObject();
          if (obj) {
            fireSegmentEvent(trackObject.name ?? "Marketing CTA Clicked", obj);
          }
        } catch {
          // tracking error safely ignored
        }
      }
      onClick?.(event);
    });
  };

  const LabelOrLoader = () => {
    return loading && !["Right", "Left"].includes(icon || "") ? (
      <span className={`Only ${size} ${level} button-loading-wrap`}>{LoadingIcon}</span>
    ) : (
      <TextElementNova
        text={label || ""}
        element="span"
        className={`${copyClassName} ${level} ${clsName} label`}
      />
    );
  };

  const wrapperClassNames = [
    className.replace(/undefined/g, ""),
    fontColor,
    level,
    size,
    icon || "Noicon",
    loading ? "loading" : "",
    disabledCls,
    "button-nova",
    onDarkBackground && "on-dark-background",
    onImageBackground && "on-image-background",
  ].join(" ");

  const sharedContent = (
    <>
      {["Only", "Left"].includes(icon || "") &&
        (loading ? (
          <span className={`left ${level} button-loading-wrap`}>{LoadingIcon}</span>
        ) : (
          <span className={`left ${level} ${disabledCls} button-arrow-wrap`}>
            {CustomIcon ?? ArrowIcon}
          </span>
        ))}

      {icon === "Only" ? null : LabelOrLoader()}
      {srlabel && <span className="aria-invisible">{srlabel}</span>}
      {!label && children && <div className="inline-block">{children}</div>}

      {icon === "Right" &&
        (loading ? (
          <span className={`right ${level} button-loading-wrap`}>{LoadingIcon}</span>
        ) : (
          <span className={`right ${level} ${disabledCls} button-arrow-wrap`}>
            {CustomIcon ?? ArrowIcon}
          </span>
        ))}
    </>
  );

  if (ctaLink) {
    return (
      <LinkHandler
        path={ctaLink}
        className={wrapperClassNames}
        onClick={handleClick}
        ariaDisabled={isDisabled}
      >
        <span style={{ width }} aria-label={srlabel || label} className={`flex`} {...props}>
          {sharedContent}
        </span>
      </LinkHandler>
    );
  }

  return (
    <button
      type={type}
      aria-label={srlabel || label}
      style={{ width }}
      disabled={isDisabled}
      className={wrapperClassNames}
      onClick={handleClick}
      {...props}
    >
      {sharedContent}
    </button>
  );
};
